/* eslint-disable no-console */
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { CHAIN_ID } from "@mezo/sdk"
import { useGetAccountByAddress, useRefreshPassport } from "@mezo-org/passport"
import { usePostHog } from "posthog-js/react"
import { useEnsName, useSwitchChain } from "wagmi"

import {
  connectWalletAccount,
  fetchWalletAccount,
  selectMats,
  selectOnboardingStep,
  selectWalletAddress,
  selectWalletBalances,
  setConnectedChainId,
  setLoadingAccountData,
  setOnboardingStep,
  setOpenModal,
  updateAccountCreationStatus,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  ENABLE_SEPARATE_MEZO_CHAINS,
  MEZO_NETWORK,
  SDK_NETWORK,
} from "#/shared/constants"
import { FlowContext, WalletContext } from "#/shared/context"
import { OnboardingStep } from "#/shared/types"

import { useConnectWalletMezoAccount } from "./onboarding"
import { useDAppRoute } from "./routes"
import { useValidateSigners } from "./validation"
import { useWalletDisconnect } from "./wallet"

const useWalletAccountChange = () => {
  const posthog = usePostHog()

  // Use refs for values that need stability between renders
  const lastAccount = useRef<string | undefined>(undefined)
  const isChangingWallet = useRef(false)
  const switchedChain = useRef(false)

  const walletAddress = useDappSelector(selectWalletAddress)
  const onboardingStep = useDappSelector(selectOnboardingStep)

  const dispatch = useDappDispatch()

  const { address, chain, signerEthersV5, signerEthersV6, signerMezo } =
    useContext(WalletContext)

  const { isBitcoinFlow } = useContext(FlowContext)

  const { data: name } = useEnsName({ address })

  const { switchChain } = useSwitchChain()
  const isDAppRoute = useDAppRoute()
  const { hasValidSigners } = useValidateSigners()

  const { signInWithWallet } = useConnectWalletMezoAccount()
  const { disconnect } = useWalletDisconnect()

  const { refetch: getCurrentAccount } = useGetAccountByAddress(
    walletAddress ?? "",
    { enabled: false },
  )

  const handleChainSwitch = useCallback(() => {
    // We only want to switch chain on initial connect
    if (switchedChain.current) return

    switchChain({
      chainId:
        CHAIN_ID[ENABLE_SEPARATE_MEZO_CHAINS ? MEZO_NETWORK : SDK_NETWORK],
    })
    switchedChain.current = true
  }, [switchChain])

  const handleWalletChange = useCallback(async () => {
    const hasSigners = address && signerEthersV5 && signerEthersV6 && signerMezo

    if (!hasSigners) {
      disconnect()
      return
    }

    try {
      isChangingWallet.current = true

      // First we need to connect wallet account in redux so we can
      // access wallet address
      await dispatch(
        connectWalletAccount({
          name,
          signerEthersV5,
          signerEthersV6,
          signerMezo,
          isBitcoinSigner: isBitcoinFlow,
        }),
      )

      // We are checking whether connected account exists in database
      const { data } = await getCurrentAccount()
      const accountExists = !!data

      // Based on account existence we either allow to only connect the wallet
      // and redirect to Sign up flow or we allow users to connect and sign message
      // no matter what flow has been chosen
      await dispatch(
        updateAccountCreationStatus({
          chainId: chain?.id ?? null,
          accountExists,
        }),
      )

      // Identify user in Posthog once user is connected
      posthog.identify(address, { wallet_address: address })

      if (accountExists) {
        const success = await signInWithWallet(address)

        if (success) {
          dispatch(setLoadingAccountData(true))
          await dispatch(fetchWalletAccount())
          dispatch(setLoadingAccountData(false))
        }

        handleChainSwitch()
      } else if (onboardingStep < OnboardingStep.ACCOUNT_NOT_FOUND) {
        dispatch(setOpenModal("onboarding"))
        dispatch(setOnboardingStep(OnboardingStep.CREATE_ACCOUNT))
      }
    } catch (error) {
      // TODO: weird state, wagmi has connection, we don't, let's disconnect
      console.error("Error connecting wallet", error)
      disconnect()
      switchedChain.current = false
    } finally {
      // After action is completed we need to reset the state
      isChangingWallet.current = false
    }
  }, [
    handleChainSwitch,
    address,
    chain?.id,
    disconnect,
    dispatch,
    getCurrentAccount,
    isBitcoinFlow,
    name,
    posthog,
    signInWithWallet,
    onboardingStep,
    signerEthersV5,
    signerEthersV6,
    signerMezo,
  ])

  useEffect(() => {
    if (!isDAppRoute || isChangingWallet.current) {
      return () => {}
    }

    const handleChange = async () => {
      // Workaround. If the address exists it means that the MetaMask will
      // autoconnect in one of the next iterations so the fetch is not
      // completed.
      if (hasValidSigners) {
        if (lastAccount.current !== address) {
          lastAccount.current = address
        }

        handleWalletChange()
      }
    }

    // Add debounce to prevent rapid consecutive calls
    const callbackTimeout = setTimeout(handleChange, 100)

    return () => clearTimeout(callbackTimeout)
  }, [handleWalletChange, hasValidSigners, isDAppRoute, lastAccount, address])
}

const useWalletChainChange = () => {
  const [lastChain, setLastChain] = useState<number | null>(null)

  const { hasValidSigners } = useValidateSigners()
  const { chain } = useContext(WalletContext)

  const dispatch = useDappDispatch()

  const handleChainChange = useCallback(async () => {
    const chainId = chain?.id

    if (chain && lastChain !== chainId && hasValidSigners) {
      setLastChain(chainId ?? null)
      dispatch(setConnectedChainId(chainId ?? null))
    }
  }, [chain, lastChain, dispatch, hasValidSigners])

  useEffect(() => {
    handleChainChange()
  }, [handleChainChange])
}

const useRefreshPassportOnAccountStateChange = () => {
  const walletBalances = useDappSelector(selectWalletBalances)
  const mats = useDappSelector(selectMats)
  const { refreshCurrentAccount } = useRefreshPassport()

  useEffect(() => {
    const handleRefresh = async () => {
      await refreshCurrentAccount()
    }

    handleRefresh()
  }, [walletBalances, mats, refreshCurrentAccount])
}

const useWallet = () => {
  useWalletAccountChange()
  useWalletChainChange()
  useRefreshPassportOnAccountStateChange()
}

export default useWallet
